import React, { useState, useEffect } from "react";
import {
  Card,
  Dropdown,
  Button,
  Alert,
  Modal,
  Form,
  Table,
} from "react-bootstrap";
import {
  FaUserCircle,
  FaUsers,
  FaEllipsisH,
  FaClock,
  FaList,
  FaInfoCircle,
  FaTimes,
} from "react-icons/fa";
import axios from "axios";
import { useUser } from "../contextProviders/userContext";
import { useNavigate } from "react-router-dom";
import LeaderProjectView from "../screens/student/LeaderProjectView";
import ProjectInfoModal from "./ProjectInfoModal";
import ProjectLeaderManagementModal from "./ProjectLeaderManagementModal";
import { API_BASE_URL } from "../api";

const MAX_FILE_SIZE_MB = 10;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const ProjectCard = ({
  project,
  isJoined,
  onJoin,
  onClick,
  isSelected,
  isFlippable = true,
}) => {
  const { user } = useUser();
  const navigate = useNavigate();
  const [isFlipped, setIsFlipped] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [joinLoading, setJoinLoading] = useState(false);
  const [showLogHoursModal, setShowLogHoursModal] = useState(false);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [hourReflection, setHourReflection] = useState("");
  const [hourEvidence, setHourEvidence] = useState([]);
  const [logHoursError, setLogHoursError] = useState(null);
  const [logHoursSuccess, setLogHoursSuccess] = useState(false);
  const [logHoursLoading, setLogHoursLoading] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [projectLogs, setProjectLogs] = useState([]);
  const [logsError, setLogsError] = useState(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showManagementModal, setShowManagementModal] = useState(false);

  // Ensure consistent type comparison for project leader check
  const isProjectLeader = Boolean(
    project?.userRole === "leader" ||
      (user?.id &&
        project?.createdBy &&
        user.id.toString() === project.createdBy.toString())
  );
  const joinPending = project?.joinStatus === "pending";
  const primaryLeader = project?.leaders?.find(
    (leader) => leader?._id?.toString() !== user?.id?.toString()
  );

  const handleViewLogs = async () => {
    console.log(project._id);
    setLogsError(null);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/projects/${project._id}/logs`,
        { headers: getAuthHeader() }
      );
      console.log(response.data);
      setProjectLogs(response.data);
      setShowLogsModal(true);
    } catch (error) {
      console.error("Error fetching project logs:", error);
      setLogsError("Failed to fetch project logs. Please try again.");
    }
  };

  const handleUpdateLog = async (logId, updates) => {
    try {
      await axios.post(
        `${API_BASE_URL}/participations/approve-hours`,
        { projectId: project._id, logId, ...updates },
        { headers: getAuthHeader() }
      );
      // Refresh logs after update
      const response = await axios.get(
        `${API_BASE_URL}/projects/${project._id}/logs`,
        { headers: getAuthHeader() }
      );
      setProjectLogs(response.data);
    } catch (error) {
      console.error("Error updating log:", error);
      setLogsError("Failed to update log. Please try again.");
    }
  };

  const handleInteraction = (e) => {
    if (!e.target.closest(".dropdown") && !e.target.closest(".btn")) {
      if (isFlippable) {
        setIsFlipped(!isFlipped);
      } else if (onClick) {
        onClick(project);
      }
    }
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(new Date(date));
  };

  const handleJoin = async () => {
    setJoinLoading(true);
    setJoinError(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/participations/join`,
        { projectId: project._id },
        { headers: getAuthHeader() }
      );
      onJoin(response.data);
    } catch (error) {
      console.error("Error joining project:", error);
      setJoinError(
        error.response?.data?.error ||
          "Failed to join project. Please try again."
      );
    } finally {
      setJoinLoading(false);
    }
  };

  const handleLeave = async () => {
    if (window.confirm(`Are you sure you want to leave ${project.name}?`)) {
      try {
        await axios.delete(
          `${API_BASE_URL}/participations/${project._id}/leave`,
          { headers: getAuthHeader() }
        );
        alert("You have successfully left the project.");
        window.location.reload();
      } catch (error) {
        console.error("Error leaving project:", error);
        alert(error.response?.data?.error || "Failed to leave project. Please try again.");
      }
    }
  };

  const handleLogHours = () => {
    setStartTime("");
    setEndTime("");
    setHourReflection("");
    setHourEvidence([]);
    setLogHoursError(null);
    setLogHoursSuccess(false);
    setShowLogHoursModal(true);
  };

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleSubmitHours = async (e) => {
    e.preventDefault();
    setLogHoursError(null);
    setLogHoursSuccess(false);

    if (!startTime || !endTime) {
      setLogHoursError("Please select both start and end times.");
      return;
    }

    setLogHoursLoading(true);
    try {
      const start = new Date(startTime);
      const end = new Date(endTime);

      if (end <= start) {
        setLogHoursError("End time must be after start time.");
        setLogHoursLoading(false);
        return;
      }

      const formData = new FormData();
      formData.append("projectId", project._id);
      formData.append("startTime", start.toISOString());
      formData.append("endTime", end.toISOString());
      formData.append("reflection", hourReflection.trim());
      hourEvidence.forEach((file) => formData.append("pictures", file, file.name));

      await axios.post(
        `${API_BASE_URL}/participations/log-hours`,
        formData,
        { headers: getAuthHeader() }
      );
      setLogHoursSuccess(true);
      setStartTime("");
      setEndTime("");
      setHourReflection("");
      setHourEvidence([]);
      setTimeout(() => {
        setShowLogHoursModal(false);
        setLogHoursSuccess(false);
      }, 2000);
    } catch (error) {
      setLogHoursError(
        error.response?.data?.error || "Failed to log hours. Please try again."
      );
    } finally {
      setLogHoursLoading(false);
    }
  };

  const cardContent = (
    <Card.Body>
      <div className="card-header-content">
        <div>
          <Card.Title className="project-title">{project.name}</Card.Title>
          <div className="title-underline"></div>
          <div className="title-underline-short"></div>
        </div>
        <Dropdown>
          <Dropdown.Toggle as={CustomToggle}>
            <FaEllipsisH size={24} className="ellipsis-icon" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            {isProjectLeader && (
              <>
                <Dropdown.Item onClick={handleViewLogs}>
                  <FaList className="me-2" />
                  View Logs
                </Dropdown.Item>
                <Dropdown.Item onClick={() => setShowManagementModal(true)}>
                  Manage Project
                </Dropdown.Item>
              </>
            )}
            {primaryLeader && isJoined && !isProjectLeader && (
              <Dropdown.Item
                onClick={() => navigate(`/messages?userId=${primaryLeader._id}`)}
              >
                Message Project Leader
              </Dropdown.Item>
            )}
            {!isJoined && user?.role === "student" ? (
              <Dropdown.Item onClick={handleJoin} disabled={joinLoading || joinPending}>
                {joinPending
                  ? "Awaiting Leader Approval"
                  : joinLoading
                    ? "Joining..."
                    : project.visibility === "private"
                      ? "Request to Join"
                      : "Join Project"}
              </Dropdown.Item>
            ) : isJoined ? (
              <>
                {user?.role === "student" && (
                  <Dropdown.Item onClick={handleLogHours}>
                    <FaClock className="me-2" />
                    Log Hours
                  </Dropdown.Item>
                )}
                {!isProjectLeader && (
                  <Dropdown.Item onClick={handleLeave} className="text-danger">
                    <FaTimes className="me-2" />
                    Leave Project
                  </Dropdown.Item>
                )}
              </>
            ) : null}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="organiser-info">
        {project.organiserType === "Individual" ? (
          <FaUserCircle size={50} className="organiser-icon individual" />
        ) : (
          <FaUsers size={50} className="organiser-icon group" />
        )}
        <div>
          <Card.Subtitle className="organiser-name">
            {project.organiserName}
          </Card.Subtitle>
          <Card.Text className="project-dates">
            {formatDate(project.startDate)} -{" "}
            {project.endDate ? formatDate(project.endDate) : "Ongoing"}
          </Card.Text>
        </div>
      </div>
      {joinError && <Alert variant="danger">{joinError}</Alert>}
    </Card.Body>
  );

  const cardBack = (
    <Card.Body>
      <div className="d-flex justify-content-between align-items-start">
        <div>
          <Card.Title>Description</Card.Title>
          <Card.Text>{project.description}</Card.Text>
        </div>
        <Button
          variant="link"
          className="info-button"
          onClick={(e) => {
            e.stopPropagation();
            setShowInfoModal(true);
          }}>
          <FaInfoCircle size={24} color="#FFF" />
        </Button>
      </div>
    </Card.Body>
  );

  const cardClasses = `project-card ${isFlippable ? "flip-card" : ""} ${
    isFlipped ? "flipped" : ""
  } ${isSelected ? "selected" : ""}`;

  return (
    <div>
      <div className={cardClasses} onClick={handleInteraction}>
        <div className="flip-card-inner">
          <div className="flip-card-front">
            <Card className={`card-content ${isSelected ? "selected" : ""}`}>
              {cardContent}
            </Card>
          </div>
          {isFlippable && (
            <div className="flip-card-back">
              <Card className="card-content back">{cardBack}</Card>
            </div>
          )}
        </div>
        {isSelected && <div className="connector"></div>}
      </div>

      <Modal
        show={showLogHoursModal}
        onHide={() => setShowLogHoursModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Log Time for {project.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Alert variant="info" className="small">
            Reflections and evidence are optional, but project leaders may need
            enough context to verify and approve your hours.
          </Alert>
          <Form onSubmit={handleSubmitHours}>
            <Form.Group className="mb-3" controlId="formStartTime">
              <Form.Label>Start Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={startTime}
                onChange={(e) => setStartTime(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formEndTime">
              <Form.Label>End Time</Form.Label>
              <Form.Control
                type="datetime-local"
                value={endTime}
                onChange={(e) => setEndTime(e.target.value)}
                required
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formHourReflection">
              <Form.Label>Reflection or activity summary (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                maxLength={5000}
                value={hourReflection}
                onChange={(event) => setHourReflection(event.target.value)}
                placeholder="Describe what you did during these hours"
              />
            </Form.Group>
            <Form.Group className="mb-3" controlId="formHourEvidence">
              <Form.Label>Evidence (Optional, max 3 files, {MAX_FILE_SIZE_MB} MB each)</Form.Label>
              <Form.Control
                type="file"
                multiple
                accept="image/jpeg,image/png,image/gif,application/pdf"
                onChange={(event) => {
                  const files = Array.from(event.target.files).slice(0, 3);
                  const oversized = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
                  if (oversized.length > 0) {
                    setLogHoursError(
                      `File "${oversized[0].name}" is too large (${(oversized[0].size / (1024 * 1024)).toFixed(1)} MB). Maximum size is ${MAX_FILE_SIZE_MB} MB per file.`
                    );
                    event.target.value = "";
                    return;
                  }
                  setLogHoursError(null);
                  setHourEvidence(files);
                }}
              />
              {hourEvidence.length > 0 && (
                <Form.Text>
                  {hourEvidence.map((f) => `${f.name} (${(f.size / (1024 * 1024)).toFixed(1)} MB)`).join(", ")}
                </Form.Text>
              )}
            </Form.Group>
            {logHoursError && <Alert variant="danger">{logHoursError}</Alert>}
            {logHoursSuccess && (
              <Alert variant="success">
                Time logged successfully! Awaiting approval.
              </Alert>
            )}
            <Button variant="primary" type="submit" disabled={logHoursLoading}>
              {logHoursLoading ? "Submitting..." : "Submit Time"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={showLogsModal}
        onHide={() => setShowLogsModal(false)}
        size="xl"
        centered>
        <Modal.Header closeButton>
          <Modal.Title>Logs for {project.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {logsError && <Alert variant="danger">{logsError}</Alert>}
          <div style={{ overflowX: "auto" }}>
            <LeaderProjectView
              projectLogs={projectLogs}
              onUpdateLog={handleUpdateLog}
              isProjectLeader={isProjectLeader}
            />
          </div>
        </Modal.Body>
      </Modal>

      <ProjectInfoModal
        show={showInfoModal}
        onHide={() => setShowInfoModal(false)}
        project={project}
      />
      <ProjectLeaderManagementModal
        show={showManagementModal}
        onHide={() => setShowManagementModal(false)}
        project={project}
        onUpdated={() => window.dispatchEvent(new Event("wcco:projects-updated"))}
      />
    </div>
  );
};

const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
  <div
    ref={ref}
    onClick={(e) => {
      e.preventDefault();
      onClick(e);
    }}
    className="custom-dropdown-toggle">
    {children}
  </div>
));

export default ProjectCard;
